import React, { useEffect, useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StepLabel from '@mui/material/StepLabel';
import {
  NavigationStep,
  ProductType,
  StepperStatus
} from '../../common/CommonTypes';

type RenewalsNavStepperProps = {
  products: ProductType[];
  pageOrder: NavigationStep[];
  navigationStatus: StepperStatus;
  currentPath: string;
};

const RenewalsNavStepper = React.memo(
  function NavStepper({
    pageOrder,
    navigationStatus,
    currentPath
  }: RenewalsNavStepperProps): JSX.Element {
    const uniquePageNames = pageOrder.filter((obj, pos, arr) => {
      return (
        arr.map((mapObj) => mapObj['pageName']).indexOf(obj['pageName']) === pos
      );
    });

    const getSteps = () => {
      uniquePageNames.sort((r1, r2) => r1.pageIndex - r2.pageIndex);

      return uniquePageNames.map((s) => ({
        stepName: s.pageName,
        completed: false
      }));
    };

    const stepsArray = [...new Set(getSteps())];

    const [steps, setSteps] = useState(stepsArray);
    const numberOfSteps = steps.length;

    const isStepCompleted = (pageName: string): boolean => {
      switch (pageName) {
        case 'Customer Details': {
          return navigationStatus.personalDetails;
        }
        case 'Personal Details': {
          return navigationStatus.personalDetails;
        }
        case 'Vehicle Details': {
          return navigationStatus.vehicle;
        }
        case 'Quote': {
          return navigationStatus.quote;
        }
        case 'Checkout': {
          return navigationStatus.checkout;
        }
        default: {
          return false;
        }
      }
    };

    const getActivePage = (): NavigationStep | undefined => {
      return pageOrder.find((step) => step.page === currentPath);
    };

    const getActiveStepIndex = (): number => {
      const activeStep = pageOrder.find((step) => step.page === currentPath);
      if (activeStep) return activeStep.pageIndex;
      else return -1;
    };

    const getLabelContent = (index: number, activeStep: boolean) => {
      const step = steps[index];

      const completedStep = step.completed;
      const confirmationStep = numberOfSteps - 1 === index && activeStep;
      const label = step.stepName;

      return (
        <StepLabel
          icon={
            activeStep && completedStep ? (
              <CheckCircleIcon className='breadcrumb-active-label' />
            ) : completedStep || confirmationStep ? (
              <CheckCircleOutlineIcon style={{ color: 'green' }} />
            ) : (
              index + 1
            )
          }>
          <span
            className={
              activeStep && !completedStep && !confirmationStep
                ? 'breadcrumb-active-label'
                : 'breadcrumb-root-label'
            }>
            {label}
          </span>
        </StepLabel>
      );
    };

    const getMobileContent = () => {
      const activePage = getActivePage();
      let index: number;
      let isErrorPage = false;
      if (activePage) index = activePage.pageIndex;
      else {
        index = -1;
        isErrorPage = true;
      }

      const indexText = isErrorPage ? '' : `Step ${index + 1} of 5`;

      const nameText = isErrorPage ? 'Error' : activePage?.pageName;

      return (
        <div style={{ paddingTop: '10px' }}>
          <span style={{ color: 'rgba(31,31,31,1)', fontSize: '14px' }}>
            {indexText}
          </span>
          <br />
          <span className='breadcrumb-mobile-active-label'>{nameText}</span>
        </div>
      );
    };

    const updateBreadcrumb = () => {
      setSteps(
        steps.map((s) => ({
          ...s,
          completed: isStepCompleted(s.stepName)
        }))
      );
    };

    useEffect(() => {
      updateBreadcrumb();
    }, [currentPath, navigationStatus]);

    const activeStepIndex = getActiveStepIndex();

    return (
      <>
        {activeStepIndex !== -1 && (
          <div>
            <div className='mobile-hide desktop-navbar'>
              <Stepper activeStep={activeStepIndex} nonLinear={true}>
                {steps.map((step, index) => {
                  return (
                    <Step key={step.stepName}>
                      {getLabelContent(index, index === activeStepIndex)}
                    </Step>
                  );
                })}
              </Stepper>
              <br />
            </div>
            <div className='desktop-hide'>
              <div className='mobile-navbar'>{getMobileContent()}</div>
            </div>
          </div>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.products.some((p) => p.isSelected === true) ===
        nextProps.products.some((p) => p.isSelected === true) &&
      prevProps.navigationStatus === nextProps.navigationStatus &&
      prevProps.currentPath === nextProps.currentPath
    ) {
      return true;
    }
    return false;
  }
);

export default RenewalsNavStepper;
